import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CAPAActionsTable = ({ actions, onUpdateStatus, onViewDetails }) => {
  const [sortField, setSortField] = useState('dueDate');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedActions = [...actions]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];

    if (sortField === 'dueDate') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-error/10 text-error',
      'in-progress': 'bg-warning/10 text-warning',
      completed: 'bg-success/10 text-success',
      overdue: 'bg-error/10 text-error'
    };
    return colors?.[status] || colors?.open;
  };

  const getPriorityIcon = (priority) => {
    const icons = {
      critical: 'AlertCircle',
      high: 'ArrowUp',
      medium: 'Minus',
      low: 'ArrowDown'
    };
    return icons?.[priority] || 'Minus';
  };

  const isOverdue = (dueDate, status) => {
    return new Date(dueDate) < new Date() && status !== 'completed';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-sm">
      <div className="p-16 md:p-20 lg:p-24 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg md:text-xl font-semibold text-foreground font-heading">
            CAPA & Actions
          </h3>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
          >
            Log New Issue
          </Button>
        </div>
      </div>
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-12 text-sm font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('issueId')}
                  className="flex items-center gap-6 hover:text-foreground transition-smooth"
                >
                  Issue ID
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-12 text-sm font-medium text-muted-foreground">
                Description
              </th>
              <th className="text-left p-12 text-sm font-medium text-muted-foreground">
                Root Cause
              </th>
              <th className="text-left p-12 text-sm font-medium text-muted-foreground">
                Action Type
              </th>
              <th className="text-left p-12 text-sm font-medium text-muted-foreground">
                Owner
              </th>
              <th className="text-left p-12 text-sm font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('dueDate')}
                  className="flex items-center gap-6 hover:text-foreground transition-smooth"
                >
                  Due Date
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left p-12 text-sm font-medium text-muted-foreground">
                Status
              </th>
              <th className="text-left p-12 text-sm font-medium text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedActions?.map((action) => (
              <tr key={action?.id} className="hover:bg-muted/50 transition-smooth">
                <td className="p-12">
                  <div className="flex items-center gap-8">
                    <Icon 
                      name={getPriorityIcon(action?.priority)} 
                      size={16} 
                      className={action?.priority === 'critical' || action?.priority === 'high' ? 'text-error' : 'text-muted-foreground'}
                    />
                    <span className="text-sm font-medium data-text">{action?.issueId}</span>
                  </div>
                </td>
                <td className="p-12">
                  <p className="text-sm text-foreground line-clamp-2 max-w-xs">
                    {action?.description}
                  </p>
                </td>
                <td className="p-12">
                  <p className="text-sm text-muted-foreground line-clamp-2 max-w-xs">
                    {action?.rootCause}
                  </p>
                </td>
                <td className="p-12">
                  <span className="text-sm text-foreground capitalize">
                    {action?.actionType}
                  </span>
                </td>
                <td className="p-12">
                  <span className="text-sm text-foreground">{action?.owner}</span>
                </td>
                <td className="p-12">
                  <div className="flex items-center gap-6">
                    <span className={`text-sm ${isOverdue(action?.dueDate, action?.status) ? 'text-error font-medium' : 'text-foreground'}`}>
                      {new Date(action.dueDate)?.toLocaleDateString()}
                    </span>
                    {isOverdue(action?.dueDate, action?.status) && (
                      <Icon name="AlertCircle" size={14} className="text-error" />
                    )}
                  </div>
                </td>
                <td className="p-12">
                  <span className={`px-8 py-4 rounded-md text-xs font-medium caption ${getStatusColor(action?.status)}`}>
                    {action?.status?.replace('-', ' ')}
                  </span>
                </td>
                <td className="p-12">
                  <div className="flex items-center gap-8">
                    <button 
                      onClick={() => onViewDetails(action?.id)}
                      className="p-6 rounded-md hover:bg-muted transition-smooth"
                    >
                      <Icon name="Eye" size={16} className="text-muted-foreground" />
                    </button>
                    <button 
                      onClick={() => onUpdateStatus(action?.id)}
                      className="p-6 rounded-md hover:bg-muted transition-smooth"
                    >
                      <Icon name="Edit" size={16} className="text-muted-foreground" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="lg:hidden divide-y divide-border">
        {sortedActions?.map((action) => (
          <div key={action?.id} className="p-16">
            <div className="flex items-start justify-between mb-12">
              <div className="flex items-center gap-8">
                <Icon 
                  name={getPriorityIcon(action?.priority)} 
                  size={16} 
                  className={action?.priority === 'critical' || action?.priority === 'high' ? 'text-error' : 'text-muted-foreground'}
                />
                <span className="text-sm font-medium data-text">{action?.issueId}</span>
              </div>
              <span className={`px-8 py-4 rounded-md text-xs font-medium caption ${getStatusColor(action?.status)}`}>
                {action?.status?.replace('-', ' ')}
              </span>
            </div>

            <p className="text-sm text-foreground mb-8 line-clamp-2">
              {action?.description}
            </p>

            <div className="space-y-6 mb-12">
              <div className="flex items-center gap-8 text-sm">
                <span className="text-muted-foreground">Type:</span>
                <span className="text-foreground capitalize">{action?.actionType}</span>
              </div>
              <div className="flex items-center gap-8 text-sm">
                <span className="text-muted-foreground">Owner:</span>
                <span className="text-foreground">{action?.owner}</span>
              </div>
              <div className="flex items-center gap-8 text-sm">
                <span className="text-muted-foreground">Due:</span>
                <span className={isOverdue(action?.dueDate, action?.status) ? 'text-error font-medium' : 'text-foreground'}>
                  {new Date(action.dueDate)?.toLocaleDateString()}
                </span>
                {isOverdue(action?.dueDate, action?.status) && (
                  <Icon name="AlertCircle" size={14} className="text-error" />
                )}
              </div>
            </div>

            <div className="flex items-center gap-8">
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                iconPosition="left"
                iconSize={16}
                onClick={() => onViewDetails(action?.id)}
                fullWidth
              >
                View Details
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Edit"
                iconPosition="left"
                iconSize={16}
                onClick={() => onUpdateStatus(action?.id)}
                fullWidth
              >
                Update
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CAPAActionsTable;